import { Assistant } from "@inrev/assistant";
import { cn } from "@portal/components/lib/utils";
import { Button, Icon } from "@portal/components/primitives";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { AdminAssistantCommandMenu } from "./AdminAssistantCommandMenu";
import { AdminAssistantSuggestions } from "./AdminAssistantSuggestions";
import {
	type AssistantCommand,
	isAssistantClearCommand,
	isAssistantCommandDraft,
	matchingAssistantCommands,
} from "./assistantCopy";

const NO_PROMPTS: string[] = [];

export const AdminAssistantComposer = ({
	disabled,
	focusKey,
	followUpPrompts = NO_PROMPTS,
	onClear,
	onSend,
	placeholder,
}: {
	disabled?: boolean;
	/** Each change moves focus to the input, once it is enabled. The first value does not. */
	focusKey?: number;
	/** Offered next questions, shown above the input. Sending one goes straight to the agent. */
	followUpPrompts?: string[];
	onClear: () => void;
	onSend: (message: string) => void;
	placeholder: string;
}) => {
	const [value, setValue] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const [menuDismissed, setMenuDismissed] = useState(false);
	const menuId = useId();
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const focusedKeyRef = useRef(focusKey);

	useEffect(() => {
		if (disabled || focusKey === focusedKeyRef.current) return;
		focusedKeyRef.current = focusKey;
		inputRef.current?.focus();
	}, [disabled, focusKey]);

	// Open while the draft is still only a command, even when nothing matches, so it can say so.
	const menuOpen = !disabled && !menuDismissed && isAssistantCommandDraft(value);
	const commands = menuOpen ? matchingAssistantCommands(value) : [];

	const changeValue = (next: string) => {
		setValue(next);
		setActiveIndex(0);
		setMenuDismissed(false);
	};

	const message = value.trim();

	const submit = () => {
		if (message === "" || disabled) return;
		changeValue("");
		if (isAssistantClearCommand(message)) {
			onClear();
			return;
		}
		onSend(message);
	};

	/** `/clear` acts at once; a skill fills the box so the underwriter can add a name after it. */
	const pick = (command: AssistantCommand) => {
		if (isAssistantClearCommand(command.command)) {
			changeValue("");
			onClear();
			return;
		}
		changeValue(`${command.command} `);
	};

	const onMenuKey = (event: KeyboardEvent<HTMLTextAreaElement>): boolean => {
		if (!menuOpen) return false;
		if (event.key === "Escape") {
			setMenuDismissed(true);
			return true;
		}
		const highlighted = commands[activeIndex];
		if (highlighted === undefined) return false;
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			const step = event.key === "ArrowDown" ? 1 : -1;
			setActiveIndex((activeIndex + step + commands.length) % commands.length);
			return true;
		}
		if ((event.key === "Tab" || event.key === "Enter") && !event.shiftKey) {
			pick(highlighted);
			return true;
		}
		return false;
	};

	const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		// An IME's Enter confirms the character being composed; it must not pick or send.
		if (event.nativeEvent.isComposing) return;
		if (onMenuKey(event)) {
			event.preventDefault();
			return;
		}
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			submit();
		}
	};

	return (
		<div className="flex shrink-0 flex-col gap-2 border-t border-bc-default px-3.5 pt-2.5 pb-3">
			{!menuOpen && (
				<AdminAssistantSuggestions
					className="flex flex-wrap gap-1.5"
					disabled={disabled}
					onSelect={onSend}
					prompts={followUpPrompts}
					testId="assistant-follow-ups"
				/>
			)}
			<div className="relative">
				{menuOpen && (
					<AdminAssistantCommandMenu
						activeIndex={activeIndex}
						commands={commands}
						draft={value}
						id={menuId}
						onHighlight={setActiveIndex}
						onPick={pick}
					/>
				)}
				<div
					className={cn(
						"flex flex-col gap-2.5 rounded-md border border-bc-strong bg-surface-base px-2.5 pt-2.5 pb-2 focus-within:border-brand-accent",
						menuOpen && "border-brand-accent",
					)}
				>
					<textarea
						role="combobox"
						aria-activedescendant={
							menuOpen && commands.length > 0 ? `${menuId}-${activeIndex}` : undefined
						}
						aria-autocomplete="list"
						aria-controls={menuOpen ? menuId : undefined}
						aria-expanded={menuOpen}
						className="min-h-11 w-full resize-none bg-transparent text-[14px] leading-[20px] text-primary placeholder:text-tertiary focus:outline-none"
						data-testid="assistant-composer"
						disabled={disabled}
						maxLength={Assistant.Dtos.Message.CHAR_LIMIT}
						onChange={(event) => changeValue(event.target.value)}
						onKeyDown={onKeyDown}
						placeholder={placeholder}
						ref={inputRef}
						value={value}
					/>
					<div className="flex items-center justify-end">
						<Button
							color="primary"
							data-testid="assistant-send"
							disabled={disabled || message === ""}
							leadingIcon={<Icon size="micro" type="paper-airplane" />}
							onClick={submit}
							size="sm"
						>
							Send
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
